import { ChangeDetectionStrategy, Component, effect, input, output, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatButtonModule } from "@angular/material/button";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { MartingaleStrategyOptions, MartingaleStrategyBidInfo } from "../../../_types/bot.type";
import { StrategyTypes } from "../../../_enums/strategy-types.enum";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-martingale-strategy-options-edit",
    templateUrl: "./martingale-strategy-options-edit.component.html",
    styleUrl: "./martingale-strategy-options-edit.component.scss",
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        FontAwesomeModule,
    ],
})
export class MartingaleStrategyOptionsEditComponent {
    public readonly StrategyOptions = input.required<MartingaleStrategyOptions>();
    public readonly StrategyOptionsChange = output<MartingaleStrategyOptions>();
    public readonly IsValid = output<boolean>();

    private readonly _options = signal<MartingaleStrategyOptions>({
        isDefault: false,
        rsiLowerLimit: 0,
        rsiUpperLimit: 100,
        emA200Limit: null,
        minProfitLimitForSell: 2,
        buyBelowMA200: false,
        type: StrategyTypes.Martingale,
        bids: [],
    });

    constructor() {
        effect(() => {
            const inputOptions: MartingaleStrategyOptions = this.StrategyOptions();
            this._options.set({
                ...inputOptions,
                bids: inputOptions.bids ?? [],
            });
            this.validateOptions();
        });
    }

    get options(): MartingaleStrategyOptions {
        return this._options();
    }

    public onOptionsChange(): void {
        this.StrategyOptionsChange.emit(this._options());
        this.validateOptions();
    }

    public validateOptions(): void {
        const isValid: boolean = this.isValidOptions();
        this.IsValid.emit(isValid);
    }

    private isValidOptions(): boolean {
        const opts: MartingaleStrategyOptions = this._options();

        if (!opts) {
            return false;
        }

        // Validate RSI limits
        if (
            opts.rsiLowerLimit !== null &&
            opts.rsiLowerLimit !== undefined &&
            (opts.rsiLowerLimit < 0 || opts.rsiLowerLimit > 100)
        ) {
            return false;
        }

        if (
            opts.rsiUpperLimit !== null &&
            opts.rsiUpperLimit !== undefined &&
            (opts.rsiUpperLimit < 0 || opts.rsiUpperLimit > 100)
        ) {
            return false;
        }

        if (
            opts.rsiLowerLimit !== null &&
            opts.rsiLowerLimit !== undefined &&
            opts.rsiUpperLimit !== null &&
            opts.rsiUpperLimit !== undefined &&
            opts.rsiLowerLimit >= opts.rsiUpperLimit
        ) {
            return false;
        }

        // Validate profit limit
        if (
            opts.minProfitLimitForSell !== null &&
            opts.minProfitLimitForSell !== undefined &&
            opts.minProfitLimitForSell < 0
        ) {
            return false;
        }

        // Validate bids
        if (opts.bids && opts.bids.length > 0) {
            for (const bid of opts.bids) {
                if (bid.purchaseIndex < 0 || bid.bidCoefficient <= 0 || bid.minPriceDropWithPrevBid < 0) {
                    return false;
                }
            }

            // Check for duplicate purchase indices
            const indices: number[] = opts.bids.map((bid: MartingaleStrategyBidInfo) => bid.purchaseIndex);
            const uniqueIndices = new Set<number>(indices);
            if (indices.length !== uniqueIndices.size) {
                return false;
            }
        }

        return true;
    }

    public addBid(): void {
        const currentOptions: MartingaleStrategyOptions = this._options();
        const currentBids: MartingaleStrategyBidInfo[] = currentOptions.bids ?? [];

        const maxIndex: number =
            currentBids.length > 0
                ? Math.max(...currentBids.map((b: MartingaleStrategyBidInfo) => b.purchaseIndex))
                : -1;

        const newBid: MartingaleStrategyBidInfo = {
            purchaseIndex: maxIndex + 1,
            bidCoefficient: 1,
            minPriceDropWithPrevBid: 0,
        };

        this._options.update((opts: MartingaleStrategyOptions) => ({
            ...opts,
            bids: [...currentBids, newBid],
        }));
        this.onOptionsChange();
    }

    public removeBid(purchaseIndex: number): void {
        const currentOptions: MartingaleStrategyOptions = this._options();
        const currentBids: MartingaleStrategyBidInfo[] = currentOptions.bids ?? [];

        this._options.update((opts: MartingaleStrategyOptions) => ({
            ...opts,
            bids: currentBids.filter((bid: MartingaleStrategyBidInfo) => bid.purchaseIndex !== purchaseIndex),
        }));
        this.onOptionsChange();
    }

    public onBidChange(): void {
        this.onOptionsChange();
    }
}
